import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import {
  FOUR_EYES_SOURCE_KEYS,
  buildFourEyesInbox,
  fourEyesTotal,
  loadFourEyesInbox,
  marketDisplayName,
  type FourEyesCounts,
  type FourEyesLine,
} from '../../src/index';
import { useAdmin } from '../session';
import { useScopeMarket } from '../components/scope-market';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { Badge, Notice, PageHeader, Panel, Table, Ink, Text, Stack, EmptyCell, AUTO_REFRESH_MS, useAutoRefresh } from '@vello/web-theme';

/**
 * Aprovações pendentes — the four-eyes inbox (W7-CASH-HANDOVER-CONSOLE, D411; Admin checklist #61).
 *
 * One line per queue where a change waits for a SECOND person — governed settings changes, driver payouts, cash
 * handovers, refunds and credits, country packs — each opening the screen where it is decided. It is a CLIENT
 * aggregation of the reads those screens already make (`loadFourEyesInbox`); no server read was added.
 *
 * - **Fail closed per source** (W6N-14): a queue that was refused or failed says "unavailable", never "0", and the grand
 *   total is then declared incomplete. A per-market queue with no market chosen says so and was not asked (W6N-11). A
 *   read cut at its limit shows "200+".
 * - A queue the operator's grants cannot read is not shown and not asked for.
 * - What the operator may decide is counted apart from what it made itself (which waits for someone else) — the same
 *   rule the server enforces; the refund queue answers it itself.
 * - The four-eyes surfaces with no pending read to aggregate (pricing rules, merchant promotions) are named, not
 *   silently left out.
 * - No primary action: the page only reads and routes; the decisions stay on their own screens.
 */
export function FourEyesInboxScreen(): ReactElement {
  const { runtime, markets, me, grants } = useAdmin();
  const { navigate } = useRouter();
  const { t } = useT();
  // Owner 2026-10-12: the per-market queues are read for the header scope's city. With no single market in scope the
  // per-market lines say so (`needs_market`) and the market-free queues still count — so no notice replaces the page.
  const { market: scopeMarket } = useScopeMarket();
  const market = scopeMarket ?? '';
  const [readMarket, setReadMarket] = useState<string | undefined>(undefined);
  const [counts, setCounts] = useState<FourEyesCounts>(() => Object.fromEntries(FOUR_EYES_SOURCE_KEYS.map((k) => [k, undefined])) as unknown as FourEyesCounts);
  const readSeq = useRef(0);

  const load = useCallback(
    async (m: string, quiet = false): Promise<void> => {
      const seq = ++readSeq.current;
      const chosen = m.trim() !== '' ? m.trim() : undefined;
      setReadMarket(chosen);
      // A background re-read keeps the numbers on screen; a scope change starts from "loading".
      if (!quiet) setCounts(Object.fromEntries(FOUR_EYES_SOURCE_KEYS.map((k) => [k, undefined])) as unknown as FourEyesCounts);
      // The market catalog behind the country-pack line is read by the loader itself (review W7H-01): the session's
      // `markets` is `[]` after a failed read, which would count as "no DRAFT pack anywhere".
      const next = await loadFourEyesInbox(runtime, { ...(chosen !== undefined ? { market: chosen } : {}), ...(me?.id !== undefined ? { operatorId: me.id } : {}), grants });
      if (seq === readSeq.current) setCounts(next);
    },
    [runtime, me?.id, grants],
  );

  useEffect(() => {
    void load(market);
    // Reload when the header scope names another market; the background re-read keeps it current.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market]);
  useAutoRefresh(() => load(market, true), { intervalMs: AUTO_REFRESH_MS.queue });

  const lines = buildFourEyesInbox(counts, grants);
  const total = fourEyesTotal(lines);
  const scopeLabel = (line: FourEyesLine): string =>
    line.perMarket
      ? readMarket !== undefined ? t('inbox.scope.market', { market: marketDisplayName(readMarket, markets) }) : t('inbox.scope.perMarket')
      : line.key === 'country_packs' ? t('inbox.scope.countries') : t('inbox.scope.all');
  const anyUnavailable = lines.some((l) => l.state === 'unavailable');
  const anyNeedsMarket = lines.some((l) => l.state === 'needs_market');
  const anyLoading = lines.some((l) => l.state === 'loading');

  return (
    <>
      <PageHeader title={t('inbox.title')} subtitle={t('inbox.subtitle')} />

      <Panel><Stack gap={4}>
        <Ink as="p" tone="secondary">{t('inbox.explain')}</Ink>
      </Stack></Panel>

      {lines.length > 0 && !anyLoading && (
        <div>
          {anyUnavailable ? (
            <Notice tone="warning">{t('inbox.total.incomplete')}</Notice>
          ) : anyNeedsMarket ? (
            <Notice tone="neutral">{t('inbox.total.needsMarket')}</Notice>
          ) : (
            <Notice tone={total.total > 0 ? 'warning' : 'neutral'}>
              <span>{t(total.truncated ? 'inbox.total.truncated' : 'inbox.total.complete', { count: String(total.total) })}</span>
            </Notice>
          )}
        </div>
      )}

      <Panel padded={false}>
        <Table
          columns={[
            {
              key: 'queue',
              header: t('inbox.col.queue'),
              render: (l: FourEyesLine) => (
                <span>
                  {t(`inbox.source.${l.key}`)}
                  <Text role="caption" tone="tertiary" block>{scopeLabel(l)}</Text>
                </span>
              ),
            },
            {
              key: 'waiting',
              header: t('inbox.col.waiting'),
              numeric: true,
              render: (l: FourEyesLine) => {
                if (l.state !== 'ready' || l.count === undefined) {
                  return l.state === 'unavailable'
                    ? <Badge tone="danger">{t('inbox.state.unavailable')}</Badge>
                    : <Text role="caption" tone="tertiary" block>{t(`inbox.state.${l.state}`)}</Text>;
                }
                const n = String(l.count.total);
                return <span className="tnum">{l.count.truncated ? t('inbox.count.truncated', { count: n }) : n}</span>;
              },
            },
            {
              key: 'detail',
              header: t('inbox.col.detail'),
              render: (l: FourEyesLine) => {
                if (l.state !== 'ready' || l.count === undefined) return <Text role="caption" tone="tertiary" block>—</Text>;
                const { decidable, own } = l.count;
                if (decidable === undefined && (own === undefined || own === 0)) return <Text role="caption" tone="tertiary" block>—</Text>;
                return (
                  <span>
                    {decidable !== undefined && <Ink block>{t('inbox.decidable', { count: String(decidable) })}</Ink>}
                    {own !== undefined && own > 0 && <Text role="caption" tone="tertiary" block>{t('inbox.own', { count: String(own) })}</Text>}
                  </span>
                );
              },
            },
          ]}
          rows={lines}
          rowKey={(l) => l.key}
          onRowClick={(l) => navigate(l.route)}
          empty={<EmptyCell>{t('inbox.none')}</EmptyCell>}
        />
      </Panel>

      <Text as="p" role="caption" tone="tertiary">{t('inbox.notAggregated')}</Text>
    </>
  );
}
